import { useCallback, useMemo } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { useFallbackStorageValue } from "shared/hooks";

import type { hooksUsePlusHomePageUpdateIndicatorQuery } from "scmp-app/queries/__generated__/hooksUsePlusHomePageUpdateIndicatorQuery.graphql";

export const usePlusHomePageLastVisitDate = () => {
  const { set, value } = useFallbackStorageValue<number>("plusHomePageLastVisitDate");

  const track = useCallback(
    (context: { hasScmpPlusAccessRight: boolean }) => {
      if (!context.hasScmpPlusAccessRight) return;
      set(Date.now());
    },
    [set],
  );

  return {
    track,
    value,
  };
};

export function usePlusHomePageUpdateIndicator() {
  const { value: plusHomePageLastVisitDate } = usePlusHomePageLastVisitDate();

  const queryData = useLazyLoadQuery<hooksUsePlusHomePageUpdateIndicatorQuery>(
    graphql`
      query hooksUsePlusHomePageUpdateIndicatorQuery {
        dailyPulse: articles(
          filter: { typeIds: ["517498"] }
          first: 1
          orderBy: [{ field: PUBLISHED_DATE, direction: DESC }]
        ) {
          edges {
            node {
              publishedDate
            }
          }
        }
        opinionQueue: queue(filter: { name: "scmp_plus_featured_opinion" }) {
          items(first: 1) {
            edges {
              node {
                ... on Content {
                  publishedDate
                }
              }
            }
          }
        }
        highlightQueue: queue(filter: { name: "scmp_plus_home_news" }) {
          items(first: 1) {
            edges {
              node {
                ... on Content {
                  publishedDate
                }
              }
            }
          }
        }
      }
    `,
    {},
  );

  const latestItemPublishedDate = Math.max(
    ...[
      ...(queryData.dailyPulse.edges ?? []),
      ...(queryData.opinionQueue?.items?.edges ?? []),
      ...(queryData.highlightQueue?.items?.edges ?? []),
    ]
      .map(edge => edge.node.publishedDate)
      .filter(date => !(date === null || date === undefined)),
  );

  const hasUnreadPlusHomePageUpdate = useMemo(
    () => latestItemPublishedDate > (plusHomePageLastVisitDate ?? 0),
    [plusHomePageLastVisitDate, latestItemPublishedDate],
  );

  return {
    hasUnreadPlusHomePageUpdate,
  };
}
